import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { AccessLimitService } from '@modules/settings/services/access-limit.service';

const PAUSED = 'AI features, sending mail and creating new items are paused.';

/** Top bar of every page while the account is limited after a failed payment or an ended trial. It has no close control. */
@Component({
  selector: 'app-access-limit-banner',
  standalone: true,
  imports: [Button2Component, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './access-limit-banner.component.html',
})
export class AccessLimitBannerComponent {
  protected readonly accessLimit = inject(AccessLimitService);
  private readonly router = inject(Router);

  protected readonly payNow = this.accessLimit.payNow;

  protected readonly actionLabel = computed(() => {
    if (this.payNow()) {
      return 'Pay now';
    }
    return this.accessLimit.trialEndedWithoutCard() ? 'Add payment method' : 'Update Payment Method';
  });

  protected readonly message = computed(() => {
    const date = this.accessLimit.deletionDate();
    if (this.accessLimit.inherited()) {
      return (
        `Your team owner's payment didn't go through. ${PAUSED} ` +
        (date
          ? `Ask your team owner to update the payment method by ${date}.`
          : 'Ask your team owner to update the payment method.')
      );
    }
    if (this.accessLimit.trialEndedWithoutCard()) {
      return (
        `Your free trial has ended. ${PAUSED} ` +
        (date ? `Add a payment method by ${date} to keep your account.` : 'Add a payment method to keep your account.')
      );
    }
    if (this.payNow()) {
      return (
        `Your payment didn't go through. ${PAUSED} ` +
        (date ? `Pay now by ${date} to keep your account.` : 'Pay now to keep your account.')
      );
    }
    return (
      `Your payment didn't go through. ${PAUSED} ` +
      (date
        ? `Update your payment method by ${date} to keep your account.`
        : 'Update your payment method to keep your account.')
    );
  });

  protected act(): void {
    void this.router.navigateByUrl(this.accessLimit.recoveryPage());
  }
}
